import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  AddCardForm as SharedAddCardForm,
  type AddCardFormProps,
  type AddCardFormRenderModel,
  type AddCardField,
} from "@bw/shared/components";

export type MaterialAddCardFormProps = Omit<AddCardFormProps, "render">;

function HostedCardField({
  field,
  model,
}: {
  field: AddCardField;
  model: AddCardFormRenderModel;
}) {
  const details = model.fields[field];
  return (
    <Box>
      <Typography
        component="label"
        htmlFor={details.id}
        variant="body2"
        sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
      >
        {details.label}
      </Typography>
      <Box
        sx={{
          minHeight: 48,
          px: 1.5,
          display: "flex",
          alignItems: "center",
          border: 1,
          borderColor:
            model.focusedField === field ? "primary.main" : "divider",
          borderRadius: 1.5,
          bgcolor: model.fieldsLocked
            ? "action.disabledBackground"
            : "background.paper",
          opacity: model.fieldsLocked ? 0.72 : 1,
          transition: (theme) =>
            theme.transitions.create(["border-color", "box-shadow"]),
          boxShadow:
            model.focusedField === field
              ? (theme) => `0 0 0 1px ${theme.palette.primary.main}`
              : "none",
          "& > *": { width: "100%" },
        }}
      >
        {details.element}
      </Box>
    </Box>
  );
}

function AddCardFormView(model: AddCardFormRenderModel) {
  return (
    <Stack spacing={2}>
      <Box
        ref={model.fieldsRef}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
          <HostedCardField field="number" model={model} />
        </Box>
        <HostedCardField field="expiry" model={model} />
        <HostedCardField field="cvc" model={model} />
        <TextField
          id={model.postal.id}
          label="ZIP or postal code"
          autoComplete="postal-code"
          disabled={model.postal.disabled}
          inputMode="text"
          slotProps={{ htmlInput: { maxLength: 12 } }}
          onChange={(event) => model.postal.onChange(event.target.value)}
          placeholder="ZIP / postal"
          value={model.postal.value}
          size="small"
          sx={{ gridColumn: { sm: "1 / -1" }, maxWidth: { sm: 280 } }}
        />
      </Box>

      {model.error ? (
        <Alert severity="error" role="alert">
          {model.error}
        </Alert>
      ) : null}
      {model.saveDisabledReason ? (
        <Typography
          variant="body2"
          color="text.secondary"
          id={model.saveDisabledReasonId}
        >
          {model.saveDisabledReason}
        </Typography>
      ) : null}
      <Box>
        <Button
          aria-describedby={
            model.saveDisabledReason ? model.saveDisabledReasonId : undefined
          }
          type="button"
          variant="contained"
          size="small"
          onClick={model.submit.onClick}
          disabled={model.submit.disabled}
        >
          {model.submit.label}
        </Button>
      </Box>
    </Stack>
  );
}

/** Player-native MUI presentation over the authoritative shared card setup flow. */
export function MaterialAddCardForm(props: MaterialAddCardFormProps) {
  return (
    <SharedAddCardForm
      {...props}
      unavailableFallback={
        <Alert severity="error">
          Card services are unavailable. Please try again.
        </Alert>
      }
      render={(model) => <AddCardFormView {...model} />}
    />
  );
}
